import { planIds } from 'consts/planIds.consts';
import { User } from 'firebase/auth';
import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { availableLocales } from 'lang/locales';

import { firestore } from '../lib/firebase-client';
import { api } from './api.util';

const getStripeUserId = async (user: User) => {
  const stripeIdDocs = await getDocs(
    query(
      collection(firestore, 'stripe-link'),
      where('user_id', '==', user.uid)
    )
  );

  const stripeUserId = !stripeIdDocs.empty
    ? stripeIdDocs.docs[0].data().stripe_customer_id
    : undefined;

  return stripeUserId;
};

export const handleStripePriceFetching = async (
  formulas: string[],
  plans: string[],
  affiliate: string | undefined | null,
  coupon: string | undefined | null,
  user: User | null | undefined,
  currency: 'usd' | 'eur' = 'usd'
): Promise<{
  [key: string]: {
    price: number;
    basePrice: number;
    recurring: boolean;
    currency: string;
  };
}> => {
  if (user) {
    const stripeUserId = await getStripeUserId(user);

    const currencyRes = stripeUserId
      ? await api.get(`/user/${stripeUserId}/currency`)
      : undefined;

    currency =
      currencyRes && currencyRes.data && currencyRes.data.currency
        ? currencyRes.data.currency
        : 'usd';
  }

  const planIdsToUse = formulas
    .map((formula: string) => {
      return plans.map((plan: string) => planIds[formula][plan][currency]);
    })
    .flat(Infinity);

  const { data: priceData } = await api.post('/plan/prices', {
    planIds: planIdsToUse,
    couponId: coupon,
    affiliateId: affiliate,
  });

  if (!priceData) {
    throw new Error('No price data');
  }

  const toReturn: any = {};

  planIdsToUse.map((planId: string) => {
    const priceObj = priceData[planId];

    if (!priceObj) return;

    const basePrice =
      priceObj.basePrice !== priceObj.totalPrice ? priceObj.basePrice : null;
    const price = priceObj.totalPrice ? priceObj.totalPrice : null;
    const recurring = priceObj.type === 'lifetime' ? null : priceObj.type;

    toReturn[planId] = {
      price,
      basePrice,
      recurring,
      currency,
    };
  });

  return toReturn;
};

export const checkoutFunction = async (
  formula: string,
  plan: string,
  affiliate: string | undefined,
  coupon: string | undefined,
  user: User
): Promise<string | undefined> => {
  const stripeUserId = await getStripeUserId(user);

  let data: any = {
    formula: formula,
    plan: plan,
    userId: stripeUserId,
  };

  if (coupon) {
    data = { ...data, couponId: coupon };
  }

  if (affiliate) {
    data = { ...data, affiliateId: affiliate };
  }

  const checkoutRes = await api.post('/payment/checkout', data);

  if (checkoutRes.data && checkoutRes.data.url) {
    return checkoutRes.data.url;
  }

  return undefined;
};

export const onLoginCustomFunction = async (
  user: User,
  locale: (typeof availableLocales)[number]
) => {
  const stripeUserId = await getStripeUserId(user);

  if (!stripeUserId) {
    //If the user doesn't have a stripe account, we create one
    await api.post('/user', {
      email: user.email,
      locale: locale,
    });
  }
};

export const getAcademyLink = (path = '/', locale = 'fr') => {
  const url = process.env.NEXT_PUBLIC_ACADEMY_URL as string;

  if (locale === 'fr') return url + path;

  const splitted = url.split('/');
  splitted.splice(-1, 0, locale);

  return splitted.join('/') + path;
};

export const updateAmbassadorLink = (keyId: string, value: string) => {
  return updateDoc(doc(firestore, 'keys', keyId), {
    amabassadorLink: value,
  });
};

export const getStripePortalLink = async (
  user: User,
  locale: string,
  portalType:
    | 'CancelSub'
    | 'CancelSubNow'
    | 'DisplayInvoices'
    | 'UpdateUser'
    | 'All'
) => {
  const queryStripeId = query(
    collection(firestore, 'stripe-link'),
    where('user_id', '==', user.uid)
  );
  const stripeIdSnapshot = await getDocs(queryStripeId);

  if (stripeIdSnapshot.empty) {
    //TODO: Add notifications
    return;
  }

  const stripeId = stripeIdSnapshot.docs[0].data().stripe_customer_id;

  const {
    data: { sessionUrl },
  } = await api.get(`/user/${stripeId}/create-portal`, {
    params: {
      returnUrl: process.env.NEXT_PUBLIC_SELF_URL,
      locale: locale.toLowerCase(),
      portalType,
    },
  });

  return sessionUrl;
};
